import { RecentVIPRecord } from "@/app/api/users/users.dto";
import ProfilePicture from "@/components/common/profile-picture";
import VerifiedBadgeCheck from "@/components/common/verified-badge-check";
import { ImageIcon } from "lucide-react";
import { countryISOs } from "@/lib/constants";
import Link from "next/link";
import { cn } from "@/lib/utils";

function getCountryIso(location: string) {
	return countryISOs?.[location as keyof typeof countryISOs];
}

const PeopleTile = (props: RecentVIPRecord) => {
	const {
		name,
		username,
		howDoYouIdentifyYourself,
		company,
		profileImage,
		location,
		isFounder,
		isVerified,
	} = props;

	const countryIso = getCountryIso(location)?.toLowerCase();
	function getShorthandName(name: string) {
		if (!name || name.split(" ").length < 3) {
			return name;
		}

		const parts = name.split(" ");
		const firstName = parts[0];
		const lastName = parts[parts.length - 1];
		const middleNames = parts.slice(1, -1);

		if (middleNames.length > 1) {
			const middleInitials = middleNames.map((middleName) => middleName[0] + ".").join(" ");
			return `${firstName} ${middleInitials} ${lastName}`;
		} else {
			return name;
		}
	} // If name has more than three words shorten the middle names

	return (
		<Link
			href={`/${username}`}
			className="mx-auto flex w-32 cursor-pointer flex-col items-center justify-center rounded-lg text-center"
		>
			<div className="mx-auto flex h-24 w-24 items-center justify-center rounded-xl">
				{profileImage ? (
					<ProfilePicture
						profilePictureUrl={profileImage}
						alternateText={username}
						className={{
							container: " h-12 w-12 rounded-full p-1 lg:h-16 lg:w-16",
							image: "h-10 w-10 rounded-full lg:h-14 lg:w-14",
							badgeImage: "-bottom-6 h-12 w-12 lg:-bottom-8 lg:h-16 lg:w-16",
						}}
						isVerified={isVerified}
						isFounder={isFounder}
					/>
				) : (
					<ImageIcon className="h-5 w-5 text-gray-400" />
				)}
			</div>

			<div className="mt-1 flex items-center gap-1 text-center">
				<h6 className="relative w-min break-words text-sm font-semibold">
					<span>{getShorthandName(name as string)}</span>

					{isFounder && isVerified && (
						<div className="absolute -right-6 top-1/2 -translate-y-1/2">
							<VerifiedBadgeCheck iconClassName="w-5 h-5" />
						</div>
					)}
				</h6>
			</div>

			<div className="my-1 flex items-center gap-2">
				<span className="w-min break-words text-center text-xs font-medium text-gray-400">
					{howDoYouIdentifyYourself}
				</span>

				<span
					className={cn(
						"h-full rounded-sm",
						countryIso && location ? `fi fi-${countryIso}` : "hidden"
					)}
				></span>
			</div>

			<div className="company-overflow max-w-full hyphens-auto break-words px-2 text-center text-xs font-medium text-gray-800">
				{company}
			</div>
		</Link>
	);
};

export default PeopleTile;
